import React, { useEffect, useState } from 'react';
import { Sparkles, TrendingUp, Award } from 'lucide-react';
import * as Icons from 'lucide-react';
import { getDomainRank, calculateDomainLevelInfo } from '../utils/levelCalculation';

interface DomainLevelUpCelebrationProps {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  categoryIcon: string | React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  oldLevel: number;
  newLevel: number;
  oldTotalLevel: number;
  newTotalLevel: number;
  newPoints: number;
  onClose: () => void;
}

export function DomainLevelUpCelebration({
  categoryId,
  categoryName,
  categoryColor,
  categoryIcon,
  oldLevel,
  newLevel,
  oldTotalLevel,
  newTotalLevel,
  newPoints,
  onClose
}: DomainLevelUpCelebrationProps) {
  const [showAnimation, setShowAnimation] = useState(false);
  const oldRank = getDomainRank(oldTotalLevel, categoryId);
  const newRank = getDomainRank(newTotalLevel, categoryId);
  const rankChanged = oldRank.name !== newRank.name;

  const levelInfo = calculateDomainLevelInfo(newPoints);
  const isMastery = newLevel === 100;

  const IconComponent = typeof categoryIcon === 'string' && (Icons[categoryIcon as keyof typeof Icons] as any)
    ? (Icons[categoryIcon as keyof typeof Icons] as any)
    : typeof categoryIcon === 'function'
    ? categoryIcon
    : null;

  useEffect(() => {
    setTimeout(() => setShowAnimation(true), 100);
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4">
      <div
        className={`max-w-lg w-full bg-card border-2 rounded-2xl p-8 space-y-6 transform transition-all duration-500 ${
          showAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{ borderColor: categoryColor }}
      >
        <div className="text-center space-y-4">
          <div className="relative inline-block">
            <div
              className="absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse"
              style={{ backgroundColor: categoryColor }}
            />
            <div
              className="relative w-24 h-24 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${categoryColor}20`, color: categoryColor }}
            >
              {IconComponent && <IconComponent className="w-12 h-12" />}
            </div>
            <div
              className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center animate-bounce"
              style={{ backgroundColor: categoryColor }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-3xl font-bold" style={{ color: categoryColor }}>
              {categoryName}
            </h3>
            <h2 className="text-3xl font-bold text-foreground">
              {isMastery ? '🎊 Domain Mastery! 🎊' : 'Domain Level Up!'}
            </h2>
          </div>

          {rankChanged && (
            <div className="flex items-center justify-center gap-4 py-6">
              <div className="text-center space-y-1">
                <div className="text-sm text-muted-foreground">Previous Rank</div>
                <div
                  className="px-4 py-2 rounded-lg font-medium"
                  style={{ backgroundColor: `${categoryColor}15`, color: categoryColor }}
                >
                  {oldRank.name}
                </div>
              </div>

              <TrendingUp className="w-6 h-6 text-muted-foreground" />

              <div className="text-center space-y-1">
                <div className="text-sm text-muted-foreground">New Rank</div>
                <div
                  className="px-4 py-2 rounded-lg font-bold animate-pulse"
                  style={{ backgroundColor: categoryColor, color: 'white' }}
                >
                  {newRank.name}
                </div>
              </div>
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Level</span>
              <span className="font-bold text-foreground">{newLevel}</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tier</span>
              <span className="font-bold" style={{ color: categoryColor }}>
                {levelInfo.tier} {levelInfo.level}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total Level</span>
              <span className="font-bold text-foreground">{newTotalLevel}</span>
            </div>

            {newLevel < 100 && (
              <>
                <div className="space-y-1 pt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Progress to Next Level</span>
                    <span className="text-muted-foreground">
                      {Math.round(levelInfo.progress)}%
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${levelInfo.progress}%`,
                        backgroundColor: categoryColor
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {levelInfo.currentPoints} / {levelInfo.pointsForNextLevel} XP
                  </span>
                  <span className="text-muted-foreground">
                    {levelInfo.pointsToNextLevel} to go
                  </span>
                </div>
              </>
            )}

            {isMastery && (
              <div className="flex items-center justify-center gap-2 text-amber-500 py-2">
                <Award className="w-5 h-5" />
                <span className="font-bold text-sm">Domain Mastery Achieved!</span>
                <Award className="w-5 h-5" />
              </div>
            )}
          </div>

          {newLevel === 1 && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-600 font-medium">
                🎯 First level in {categoryName}! Keep building momentum.
              </p>
            </div>
          )}

          {newLevel === 10 && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <p className="text-sm text-green-600 font-medium">
                🌟 Double digits! You're making serious progress in {categoryName}.
              </p>
            </div>
          )}

          {newLevel === 50 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-600 font-medium">
                ⭐ Halfway to mastery! Your dedication to {categoryName} is inspiring.
              </p>
            </div>
          )}

          {isMastery && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-600 font-medium">
                🏆 You've reached the pinnacle of {categoryName} mastery!
              </p>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-lg font-medium text-white transition-all hover:scale-105"
          style={{ backgroundColor: categoryColor }}
        >
          Continue
        </button>
      </div>

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-20px',
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
              fontSize: `${20 + Math.random() * 20}px`,
              opacity: 0.6
            }}
          >
            {['✨', '⭐', '🌟', '💫'][Math.floor(Math.random() * 4)]}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}
